// ================================
// CUSTOMER WALLET PAGE - PHASE 2
// Buy coins, send coins, transaction history
// ================================
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useSocket } from '../../context/SocketContext';
import axios from 'axios';

// Cash App keeps its original /api/coins/buy-link route untouched; Venmo/
// Chime/Apple Pay all go through /api/coins/buy-link/:method instead —
// same admin-managed tag pool pattern, just three more of them.
const PAY_METHODS = [
  { id: 'cashapp',  label: 'Cash App' },
  { id: 'venmo',    label: 'Venmo' },
  { id: 'chime',    label: 'Chime' },
  { id: 'applepay', label: 'Apple Pay' }
];

export default function Wallet() {
  const { user }              = useAuth();
  const socket                = useSocket();
  const navigate              = useNavigate();
  const [balance, setBalance] = useState(0);
  const [rate, setRate]       = useState(1.10);
  const [feeRate, setFeeRate] = useState(10);
  const [coinAmount, setCoinAmount] = useState('');
  const [method, setMethod] = useState('cashapp');
  const [payLink, setPayLink] = useState(null);
  const [copiedPayTo, setCopiedPayTo] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tab, setTab]         = useState('buy');
  const [transfer, setTransfer] = useState({ code:'', amount:'' });
  const [transferMsg, setTransferMsg] = useState('');
  // Second security step before coins actually move — the code+amount
  // form just opens this; nothing is sent until the PIN here checks out.
  const [pinConfirmOpen, setPinConfirmOpen] = useState(false);
  const [sendPin, setSendPin] = useState('');
  const [pinError, setPinError] = useState('');
  const [receiveCode, setReceiveCode] = useState(null); // { code, expiresAt }
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [codeLoading, setCodeLoading] = useState(false);

  useEffect(() => {
    fetchBalance();
    fetchRate();
    fetchFeeRate();
    fetchPendingPurchase();
  }, []);

  useEffect(() => {
    if (!socket) return;
    // 'connect' fires on every successful connection, including an
    // automatic reconnect after a dropped connection (a Railway deploy
    // restarting the server, a phone backgrounding the tab, a network
    // blip) — none of which trigger 'rate:updated'/'feeRate:updated',
    // since those only fire for an actual admin change. Without this, a
    // tab left open across a reconnect keeps showing whatever it fetched
    // once at mount, silently stale, with no way to tell it's wrong.
    socket.on('connect', () => { fetchRate(); fetchFeeRate(); fetchBalance(); });
    socket.on('wallet:updated', ({ balance: b }) => setBalance(b));
    socket.on('rate:updated',   ({ rate: r })    => setRate(r));
    socket.on('feeRate:updated', ({ rate: r })   => setFeeRate(r));
    socket.on('coin:purchase-rejected', ({ requestId }) => {
      setPayLink(p => {
        if (p && p.requestId === requestId) {
          alert('Your coin purchase request was rejected — payment may not have been received. Please try again or contact support.');
          return null;
        }
        return p;
      });
    });
    socket.on('coin:purchase-approved', ({ requestId }) => {
      setPayLink(p => {
        if (p && p.requestId === requestId) {
          alert('✅ Your coin purchase was confirmed — coins have been added to your wallet.');
          setCoinAmount('');
          return null;
        }
        return p;
      });
    });
    return () => {
      socket.off('connect'); socket.off('wallet:updated'); socket.off('rate:updated'); socket.off('feeRate:updated');
      socket.off('coin:purchase-rejected'); socket.off('coin:purchase-approved');
    };
  }, [socket]);

  async function fetchBalance() {
    try {
      const res = await axios.get('/api/coins/balance');
      setBalance(res.data.balance || 0);
    } catch(e) { setBalance(parseFloat(user?.wallet) || 0); }
  }

  async function fetchRate() {
    try {
      const res = await axios.get('/api/coins/rate');
      setRate(res.data.rate || 1.10);
    } catch(e) {}
  }

  async function fetchFeeRate() {
    try {
      const res = await axios.get('/api/coins/fee-rate');
      setFeeRate(res.data.rate ?? 10);
    } catch(e) {}
  }

  // Restores an in-flight purchase if one exists — e.g. the customer
  // generated a link, navigated away (or just closed the tab) before
  // paying, and came back later with no other way to find it again.
  async function fetchPendingPurchase() {
    try {
      const res = await axios.get('/api/coins/my-pending-purchase');
      if (res.data.request) {
        const r = res.data.request;
        setCoinAmount(String(r.coinAmount));
        setMethod(r.method || 'cashapp');
        setPayLink({ requestId: r.requestId, link: r.link, payTo: r.cashtag ? (r.method === 'applepay' ? r.cashtag : `${r.method === 'venmo' ? '@' : '$'}${r.cashtag}`) : null, coins: r.coinAmount, base: r.base, fee: r.fee, total: r.total, instructions: r.instructions });
      }
    } catch(e) {}
  }

  async function generateLink() {
    if (!coinAmount || parseFloat(coinAmount) <= 0) return;
    setLoading(true);
    try {
      const url = method === 'cashapp' ? '/api/coins/buy-link' : `/api/coins/buy-link/${method}`;
      const res = await axios.post(url, { coinAmount: parseFloat(coinAmount) });
      setPayLink(res.data);
    } catch(e) { alert(e.response?.data?.error || 'Failed to generate link'); }
    finally { setLoading(false); }
  }

  function openPinConfirm(e) {
    e.preventDefault();
    if (!transfer.code || !transfer.amount) return;
    setPinError('');
    setSendPin('');
    setPinConfirmOpen(true);
  }

  async function confirmSendWithPin() {
    if (!sendPin) return;
    setLoading(true);
    setPinError('');
    try {
      const res = await axios.post('/api/coins/transfer', {
        code: transfer.code,
        amount: parseFloat(transfer.amount),
        pin: sendPin
      });
      setTransferMsg(`✅ ${transfer.amount} coins sent to ${res.data.recipientName || 'recipient'}!`);
      setTransfer({ code:'', amount:'' });
      setPinConfirmOpen(false);
      setSendPin('');
      fetchBalance();
    } catch(e) {
      setPinError(e.response?.data?.error || 'Transfer failed');
    } finally { setLoading(false); }
  }

  async function generateCode() {
    setCodeLoading(true);
    try {
      const res = await axios.post('/api/coins/receive-code');
      setReceiveCode(res.data);
    } catch(e) { alert('Failed to generate code'); }
    finally { setCodeLoading(false); }
  }

  useEffect(() => {
    if (!receiveCode) return;
    const tick = () => setSecondsLeft(Math.max(0, Math.round((receiveCode.expiresAt - Date.now()) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [receiveCode]);

  const coins = parseFloat(coinAmount) || 0;
  // Once a link exists, the price is a locked-in quote from the moment it
  // was generated — if the admin changes the coin rate or fee % while this
  // customer still has the pay screen open, they must keep seeing (and
  // paying) the number they were originally quoted, not one that silently
  // drifts out from under them.
  const base  = payLink ? parseFloat(payLink.base)  : coins * rate;
  const fee   = payLink ? parseFloat(payLink.fee)   : base * (feeRate / 100);
  const total = payLink ? parseFloat(payLink.total) : base + fee;

  const tabStyle = (t) => ({
    flex:1, padding:'10px 0', background:'none', border:'none',
    borderBottom: tab === t ? '2px solid var(--accent-blue)' : '2px solid transparent',
    color: tab === t ? 'var(--accent-blue)' : 'var(--text-secondary)',
    fontSize:13, cursor:'pointer', fontFamily:'DM Sans, sans-serif',
    textTransform:'uppercase', letterSpacing:1
  });

  return (
    <div className="app-shell">
      {/* Header */}
      <div style={{ padding:'18px 22px 14px', borderBottom:'1px solid var(--border)' }}>
        <button className="back-btn" onClick={() => navigate('/dashboard')}>← Back</button>
        <h2 style={{ fontFamily:'Bebas Neue, sans-serif', fontSize:24, letterSpacing:2 }}>My Wallet</h2>
      </div>

      {/* Balance Card */}
      <div style={{ margin:'16px 20px', background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:18, padding:20 }}>
        <p style={{ fontSize:11, color:'var(--text-secondary)', textTransform:'uppercase', letterSpacing:1, marginBottom:6 }}>Coin Balance</p>
        <p style={{ fontFamily:'Bebas Neue, sans-serif', fontSize:44, color:'var(--accent-blue)', letterSpacing:1 }}>
          {parseFloat(balance).toFixed(0)} <span style={{ fontSize:20, color:'var(--text-secondary)' }}>coins</span>
        </p>
        <p style={{ fontSize:11, color:'var(--text-muted)', marginTop:6 }}>
          Today's rate: <span style={{ color:'var(--accent-blue)' }}>${rate.toFixed(2)}</span>/coin + {feeRate}% exchange fee
        </p>
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', margin:'0 20px', borderBottom:'1px solid var(--border)' }}>
        <button style={tabStyle('buy')} onClick={() => setTab('buy')}>Buy Coins</button>
        <button style={tabStyle('send')} onClick={() => setTab('send')}>Send Coins</button>
        <button style={tabStyle('receive')} onClick={() => setTab('receive')}>Receive Coins</button>
      </div>

      <div style={{ flex:1, overflowY:'auto', padding:'20px 22px' }}>

        {/* BUY COINS */}
        {tab === 'buy' && (
          <>
            <p style={{ fontSize:13, color:'var(--text-secondary)', marginBottom:16, lineHeight:1.6 }}>
              Enter how many coins you want to buy, then pick how you'll pay.
            </p>

            <div className="field">
              <label>Pay with</label>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:8, marginBottom:4 }}>
                {PAY_METHODS.map(m => (
                  <button key={m.id} type="button" disabled={!!payLink}
                    onClick={() => setMethod(m.id)}
                    style={{
                      padding:'10px 4px', borderRadius:10, fontSize:12, cursor: payLink ? 'default' : 'pointer',
                      fontFamily:'DM Sans, sans-serif', fontWeight: method === m.id ? 600 : 400,
                      background: method === m.id ? 'var(--bg-card)' : 'transparent',
                      color: method === m.id ? 'var(--accent-blue)' : 'var(--text-secondary)',
                      border: `1px solid ${method === m.id ? 'var(--accent-blue)' : 'var(--border)'}`,
                      opacity: payLink && method !== m.id ? 0.4 : 1
                    }}>
                    {m.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="field">
              <label>How many coins?</label>
              <input type="number" placeholder="100" value={coinAmount}
                onChange={e => { setCoinAmount(e.target.value); setPayLink(null); }} />
              {!coinAmount && (
                <p style={{ fontSize:11, color:'var(--text-muted)', marginTop:6 }}>
                  Enter an amount above — "100" is just an example, not a value.
                </p>
              )}
            </div>

            {coins > 0 && (
              <div style={{ background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:12, padding:16, marginBottom:16 }}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
                  <span style={{ fontSize:13, color:'var(--text-secondary)' }}>{coins} coins × ${rate.toFixed(2)}</span>
                  <span style={{ fontSize:13, color:'var(--text-primary)' }}>${base.toFixed(2)}</span>
                </div>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
                  <span style={{ fontSize:13, color:'var(--text-secondary)' }}>Exchange fee ({feeRate}%)</span>
                  <span style={{ fontSize:13, color:'var(--text-primary)' }}>${fee.toFixed(2)}</span>
                </div>
                <div style={{ borderTop:'1px solid var(--border)', paddingTop:8, display:'flex', justifyContent:'space-between' }}>
                  <span style={{ fontSize:14, fontWeight:500, color:'var(--text-primary)' }}>Total you pay</span>
                  <span style={{ fontFamily:'Bebas Neue, sans-serif', fontSize:18, color:'var(--accent-blue)' }}>${total.toFixed(2)}</span>
                </div>
              </div>
            )}

            {!payLink ? (
              <button className="btn-primary" onClick={generateLink} disabled={loading || !coinAmount}
                title={!coinAmount ? 'Enter a coin amount first' : undefined}>
                {loading ? 'Generating...' : `Pay with ${PAY_METHODS.find(m => m.id === method)?.label}`}
              </button>
            ) : (
              <div style={{ textAlign:'center' }}>
                <p style={{ fontSize:13, color:'var(--text-secondary)', marginBottom:16, lineHeight:1.6 }}>
                  {payLink.instructions}
                </p>
                {payLink.link ? (
                  <a href={payLink.link} target="_blank" rel="noopener noreferrer" style={{
                    display:'block', width:'100%', padding:15,
                    background:'#00c244', color:'#fff', borderRadius:14,
                    fontSize:15, fontWeight:500, textDecoration:'none',
                    textAlign:'center', marginBottom:12
                  }}>
                    Open {PAY_METHODS.find(m => m.id === (payLink.method || method))?.label} — Pay ${total.toFixed(2)}
                  </a>
                ) : (
                  <div style={{ background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:14, padding:18, marginBottom:12 }}>
                    <p style={{ fontSize:11, color:'var(--text-secondary)', textTransform:'uppercase', letterSpacing:1, marginBottom:6 }}>
                      {(payLink.method || method) === 'applepay' ? 'Send to this number' : 'Send to this tag'}
                    </p>
                    <p style={{ fontFamily:'Bebas Neue, sans-serif', fontSize:26, color:'var(--accent-blue)', letterSpacing:1, marginBottom:10 }}>
                      {payLink.payTo}
                    </p>
                    <button type="button" className="btn-secondary" onClick={() => {
                      navigator.clipboard?.writeText(payLink.payTo || '').then(() => setCopiedPayTo(true)).catch(() => {});
                    }}>
                      {copiedPayTo ? 'Copied ✓' : 'Copy'}
                    </button>
                  </div>
                )}
                <button className="btn-secondary" onClick={() => {
                  axios.post('/api/coins/buy-cancel', { requestId: payLink.requestId }).catch(() => {});
                  setPayLink(null); setCoinAmount(''); setCopiedPayTo(false);
                }}>Cancel</button>
                <p style={{ fontSize:11, color:'var(--text-muted)', marginTop:12 }}>
                  Coins will be added to your wallet once we confirm your payment — usually within a few minutes.
                </p>
              </div>
            )}
          </>
        )}

        {/* SEND COINS */}
        {tab === 'send' && (
          <>
            <p style={{ fontSize:13, color:'var(--text-secondary)', marginBottom:16, lineHeight:1.6 }}>
              Ask the recipient to generate a receive code from their Wallet → Receive Coins tab, then enter it below. You never need their Member ID or PIN.
            </p>
            <form onSubmit={openPinConfirm}>
              <div className="field">
                <label>Recipient's Receive Code</label>
                <input type="text" placeholder="123456" maxLength={6} inputMode="numeric"
                  value={transfer.code} onChange={e => setTransfer(p => ({ ...p, code: e.target.value.replace(/\D/g,'') }))} required />
              </div>
              <div className="field">
                <label>Amount (coins)</label>
                <input type="number" placeholder="0" min="1" max={balance}
                  value={transfer.amount} onChange={e => setTransfer(p => ({ ...p, amount: e.target.value }))} required />
              </div>

              {parseFloat(transfer.amount) > 0 && (
                <div style={{ background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:12, padding:14, marginBottom:16 }}>
                  <p style={{ fontSize:13, color:'var(--text-secondary)' }}>
                    Your balance after: <span style={{ color:'var(--accent-blue)' }}>
                      {Math.max(0, balance - parseFloat(transfer.amount || 0)).toFixed(0)} coins
                    </span>
                  </p>
                </div>
              )}

              {transferMsg && (
                <p style={{ fontSize:13, marginBottom:12, color: transferMsg.includes('✅') ? 'var(--accent-green)' : 'var(--accent-red)' }}>
                  {transferMsg}
                </p>
              )}

              <button className="btn-primary" type="submit"
                disabled={!transfer.code || !transfer.amount || parseFloat(transfer.amount) > balance}>
                Send Coins
              </button>
            </form>

            {parseFloat(transfer.amount) > balance && (
              <p style={{ fontSize:12, color:'var(--accent-red)', marginTop:8 }}>Insufficient balance</p>
            )}
          </>
        )}

        {/* RECEIVE COINS */}
        {tab === 'receive' && (
          <>
            <p style={{ fontSize:13, color:'var(--text-secondary)', marginBottom:16, lineHeight:1.6 }}>
              Generate a one-time code and share it with whoever is sending you coins. It's single-use and expires on its own — your Member ID and PIN stay private.
            </p>

            {!receiveCode || secondsLeft <= 0 ? (
              <button className="btn-primary" onClick={generateCode} disabled={codeLoading}>
                {codeLoading ? 'Generating...' : 'Generate Receive Code'}
              </button>
            ) : (
              <div style={{ textAlign:'center' }}>
                <div style={{ background:'var(--bg-card)', border:'1px solid var(--border)', borderRadius:18, padding:24, marginBottom:16 }}>
                  <p style={{ fontSize:11, color:'var(--text-secondary)', textTransform:'uppercase', letterSpacing:1, marginBottom:10 }}>
                    Your Receive Code
                  </p>
                  <p style={{ fontFamily:'Bebas Neue, sans-serif', fontSize:48, color:'var(--accent-blue)', letterSpacing:6 }}>
                    {receiveCode.code}
                  </p>
                  <p style={{ fontSize:12, color:'var(--text-muted)', marginTop:10 }}>
                    Expires in {Math.floor(secondsLeft / 60)}:{String(secondsLeft % 60).padStart(2,'0')}
                  </p>
                </div>
                <button className="btn-secondary" onClick={generateCode} disabled={codeLoading}>
                  {codeLoading ? 'Generating...' : 'Generate New Code'}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* PIN confirmation — second step before Send Coins actually moves
          anything, so a phone left unlocked isn't enough on its own. */}
      {pinConfirmOpen && (
        <div style={{ position:'fixed', inset:0, zIndex:60 }}>
          <div onClick={() => { setPinConfirmOpen(false); setSendPin(''); }} style={{ position:'absolute', inset:0, background:'#000000cc' }} />
          <div style={{ position:'absolute', bottom:0, left:0, right:0, background:'var(--bg-base)', borderRadius:'20px 20px 0 0', padding:'24px 20px 36px' }}>
            <div style={{ width:40, height:4, background:'var(--border)', borderRadius:2, margin:'0 auto 16px' }} />
            <p style={{ fontFamily:'Bebas Neue, sans-serif', fontSize:20, letterSpacing:1, marginBottom:10 }}>
              Confirm with your PIN
            </p>
            <p style={{ fontSize:13, color:'var(--text-secondary)', lineHeight:1.6, marginBottom:20 }}>
              Send {transfer.amount} coins using code {transfer.code}?
            </p>

            <div className="field">
              <label>Your PIN</label>
              <input type="password" placeholder="••••" maxLength={6} inputMode="numeric" autoFocus
                value={sendPin} onChange={e => setSendPin(e.target.value.replace(/\D/g,''))} />
            </div>

            {pinError && <p style={{ fontSize:13, color:'var(--accent-red)', marginBottom:12 }}>{pinError}</p>}

            <button className="btn-primary" onClick={confirmSendWithPin} disabled={loading || !sendPin}>
              {loading ? 'Sending...' : 'Confirm & Send'}
            </button>
            <button onClick={() => { setPinConfirmOpen(false); setSendPin(''); }} style={{ display:'block', width:'100%', marginTop:10, padding:12, background:'none', border:'1px solid var(--border)', borderRadius:12, color:'var(--text-secondary)', fontSize:13, cursor:'pointer', fontFamily:'DM Sans, sans-serif' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Nav */}
      <div className="nav-bar">
        <button className="nav-item" onClick={() => navigate('/dashboard')}><span style={{ fontSize:17 }}>🏠</span>Home</button>
        <button className="nav-item" onClick={() => navigate('/order')}><span style={{ fontSize:17 }}>🛒</span>Order</button>
        <button className="nav-item active"><span style={{ fontSize:17 }}>💰</span>Wallet</button>
        <button className="nav-item" onClick={() => navigate('/rewards')}><span style={{ fontSize:17 }}>🏆</span>Rewards</button>
        <button className="nav-item" onClick={() => navigate('/track')}><span style={{ fontSize:17 }}>🕐</span>Track</button>
      </div>
    </div>
  );
}
